// The staff routes (architecture plan §11.3; build plan P5): the queue with its filters and
// search, stats, the full detail including internal notes, the portal-side patch, replies with a
// visibility that cannot be mistaken, and raising a request on behalf of a caller with
// provisional binding.

import { z } from "zod";
import { getCategory } from "./categories";
import { LINK_TYPES, RequestStatusSchema, STATE_CATEGORIES } from "./enums";
import { RequestFormFieldsSchema } from "./intake";
import {
  CreateRequestResponseSchema,
  RequestDetailSchema,
  RequestSummarySchema,
  ThreadMessageSchema,
  validateFollowUpAnswers,
} from "./requests-api";

export const PROVISIONAL_BINDING_DAYS = 30;

export const TRIAGE_OUTCOME_FILTERS = [
  "written",
  "reused",
  "queued_for_human",
  "stopped",
  "failed",
  "none",
] as const;

export const StaffListQuerySchema = z.object({
  /** Comma-separated statuses. */
  status: z.string().optional(),
  category: z.string().optional(),
  priority: z.coerce.number().int().min(1).max(4).optional(),
  assignedToUserId: z.uuid().optional(),
  unassigned: z.enum(["true", "false"]).optional(),
  requesterUserId: z.uuid().optional(),
  triageOutcome: z.enum(TRIAGE_OUTCOME_FILTERS).optional(),
  /** Public id, subject, work item id, or requester email. */
  q: z.string().trim().max(200).optional(),
  cursor: z.string().optional(),
  limit: z.string().optional(),
});
export type StaffListQuery = z.infer<typeof StaffListQuerySchema>;

export const StaffRequesterSchema = z.object({
  id: z.uuid().nullable(),
  displayName: z.string().nullable(),
  upn: z.string().nullable(),
});

export const StaffProvisionalSchema = z
  .object({
    upn: z.string(),
    expiresAt: z.iso.datetime(),
    verification: z.string().nullable(),
    expired: z.boolean(),
  })
  .nullable();

export const StaffRequestSummarySchema = RequestSummarySchema.extend({
  requester: StaffRequesterSchema,
  raisedBy: z.object({ id: z.uuid(), displayName: z.string().nullable() }).nullable(),
  priority: z.number().int().nullable(),
  assignedToDisplay: z.string().nullable(),
  assignedToUserId: z.uuid().nullable(),
  triageOutcome: z.string().nullable(),
  provisional: StaffProvisionalSchema,
  pendingBoardPatch: z.record(z.string(), z.unknown()).nullable(),
  secretRedacted: z.boolean(),
  version: z.number().int(),
});
export type StaffRequestSummary = z.infer<typeof StaffRequestSummarySchema>;

export const StaffMessageSchema = ThreadMessageSchema.extend({
  isInternal: z.boolean(),
  authorUserId: z.uuid().nullable(),
  authorIdentity: z.string().nullable(),
  authorDisplayName: z.string().nullable(),
});
export type StaffMessage = z.infer<typeof StaffMessageSchema>;

export const StaffLinkSchema = z.object({
  workItemId: z.number().int(),
  linkType: z.enum(LINK_TYPES),
  origin: z.enum(["created", "reused"]),
  stateName: z.string().nullable(),
  stateCategory: z.enum(STATE_CATEGORIES).nullable(),
  lastRev: z.number().int().nullable(),
  workItemUrl: z.string().nullable(),
});

export const StaffRunSummarySchema = z
  .object({
    id: z.uuid(),
    outcome: z.string().nullable(),
    queueReason: z.string().nullable(),
    stopReason: z.string().nullable(),
    startedAt: z.iso.datetime(),
    reviewedAt: z.iso.datetime().nullable(),
  })
  .nullable();

export const StaffRequestDetailSchema = StaffRequestSummarySchema.extend({
  description: RequestDetailSchema.shape.description,
  department: RequestDetailSchema.shape.department,
  location: RequestDetailSchema.shape.location,
  onsiteOrRemote: RequestDetailSchema.shape.onsiteOrRemote,
  callback: RequestDetailSchema.shape.callback,
  affectedSystemOrDevice: RequestDetailSchema.shape.affectedSystemOrDevice,
  onset: RequestDetailSchema.shape.onset,
  errorText: RequestDetailSchema.shape.errorText,
  troubleshootingAttempted: RequestDetailSchema.shape.troubleshootingAttempted,
  businessImpact: RequestDetailSchema.shape.businessImpact,
  neededBy: RequestDetailSchema.shape.neededBy,
  sponsor: RequestDetailSchema.shape.sponsor,
  followUpAnswers: RequestDetailSchema.shape.followUpAnswers,
  withdrawnAt: RequestDetailSchema.shape.withdrawnAt,
  boardState: z.string().nullable(),
  messages: z.array(StaffMessageSchema),
  links: z.array(StaffLinkSchema),
  latestRun: StaffRunSummarySchema,
});
export type StaffRequestDetail = z.infer<typeof StaffRequestDetailSchema>;

export const StaffListSchema = z.object({
  items: z.array(StaffRequestSummarySchema),
  nextCursor: z.string().nullable(),
});
export type StaffList = z.infer<typeof StaffListSchema>;

export const StaffStatsSchema = z.object({
  byStatus: z.record(RequestStatusSchema, z.number().int()),
  open: z.number().int(),
  unsynced: z.number().int(),
  unassigned: z.number().int(),
  queuedForHuman: z.number().int(),
  unboundProvisional: z.number().int(),
});
export type StaffStats = z.infer<typeof StaffStatsSchema>;

/** Portal-side status changes staff may make; everything else is the board's (§11.6). */
export const STAFF_STATUS_CHANGES = ["in_progress", "waiting_on_user"] as const;

export const StaffPatchBodySchema = z
  .object({
    /** The request's current version; a stale one is refused rather than overwritten. */
    version: z.number().int().positive(),
    status: z.enum(STAFF_STATUS_CHANGES).optional(),
    /** Requested on the board through the outbox; shown as pending until applied (§11.6). */
    priority: z.number().int().min(1).max(4).nullable().optional(),
    assignedToUserId: z.uuid().nullable().optional(),
  })
  .strict()
  .refine(
    (b) => b.status !== undefined || b.priority !== undefined || b.assignedToUserId !== undefined,
    {
      message: "nothing to change",
    },
  );
export type StaffPatchBody = z.infer<typeof StaffPatchBodySchema>;

export const StaffReplyBodySchema = z
  .object({
    body: z.string().trim().min(1).max(20000),
    /** Explicit on every reply: `requester` reaches the requester, `internal` never does. */
    visibility: z.enum(["requester", "internal"]),
  })
  .strict();
export type StaffReplyBody = z.infer<typeof StaffReplyBodySchema>;

/**
 * Raising a request on behalf of a caller (§7.1, §11.3). Any configured category, including the
 * phone-only ones the requester form refuses. The caller's verified UPN binds the request at
 * their first sign-in; the call-taker records how identity was verified.
 */
export const StaffRaiseBodySchema = RequestFormFieldsSchema.extend({
  subject: z.string().trim().min(3).max(200),
  description: z.string().trim().min(10).max(20000),
  department: z.string().trim().min(1).max(200),
  location: z.string().trim().min(1).max(200),
  callback: z.string().trim().min(3).max(60),
  affectedSystemOrDevice: z.string().trim().max(500).default(""),
  onset: z.string().trim().max(200).default(""),
  errorText: z.string().max(20000).default(""),
  troubleshootingAttempted: z.string().max(20000).default(""),
  neededBy: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
  sponsor: z.string().trim().max(200).nullable().default(null),
  sensitivityAcknowledged: z.boolean().default(true),
  caller: z
    .object({
      upn: z
        .string()
        .trim()
        .min(3)
        .max(320)
        .regex(/^[^@\s]+@[^@\s]+$/, "expected a UPN"),
      displayName: z.string().trim().max(200).optional(),
      verification: z.string().trim().min(1).max(500),
    })
    .strict(),
})
  .strict()
  .superRefine((body, ctx) => {
    const category = getCategory(body.category);
    if (!category) {
      ctx.addIssue({ code: "custom", path: ["category"], message: "unknown category" });
      return;
    }
    for (const issue of validateFollowUpAnswers(category, body.followUpAnswers)) {
      ctx.addIssue({
        code: "custom",
        path: ["followUpAnswers", issue.path],
        message: issue.message,
      });
    }
  });
export type StaffRaiseBody = z.infer<typeof StaffRaiseBodySchema>;
export type StaffRaiseBodyInput = z.input<typeof StaffRaiseBodySchema>;

export const StaffRaiseResponseSchema = CreateRequestResponseSchema.extend({
  /** True when the caller already had an identity and the request is theirs immediately. */
  bound: z.boolean(),
  provisionalExpiresAt: z.iso.datetime().nullable(),
});
export type StaffRaiseResponse = z.infer<typeof StaffRaiseResponseSchema>;

export const StaffReplyResponseSchema = z.object({
  id: z.uuid(),
  visibility: z.enum(["requester", "internal"]),
  status: RequestStatusSchema,
  redacted: z.boolean(),
});

export const StaffPatchResponseSchema = z.object({
  id: z.uuid(),
  status: RequestStatusSchema,
  version: z.number().int(),
  pendingBoardPatch: z.record(z.string(), z.unknown()).nullable(),
});
